import { ReactElement, memo, useMemo } from 'react';

import { ErrorBoundary } from ':components';

import { ServiceComponent } from './components/ServiceItem';
import { ErrorComponent } from './components/ErrorComponent';
import { LoadingComponent } from './components/LoadingComponent';

import styles from './styles.module.scss';

import { ServiceItem } from ':types';
import { NORMAL_SCREEN_WIDTH, SMALL_SCREEN_WIDTH } from ':constants';
import { useWindowWidth } from ':hooks';

type ServicesListProps = {
  items?: ServiceItem[];
  error: boolean;
  isLoading: boolean;
  refetch: () => void;
};
export const ServicesList = memo(function ServicesListComponent({
  items,
  error,
  isLoading,
  refetch
}: ServicesListProps): ReactElement {
  const getColumnsAmount = (width: number): number => {
    const baseColumnsAmount = 2;

    // List of predefined columns amount
    const columnsAmountByWidth: Record<number, number> = {
      [SMALL_SCREEN_WIDTH]: 1,
      [NORMAL_SCREEN_WIDTH]: 2,
    };

    // If less than screen width return different value
    for (const key in columnsAmountByWidth) {
      if (width <= Number(key)) return columnsAmountByWidth[key];
    }

    // Default columns amount
    return baseColumnsAmount;
  };

  const generateColumns = (
    items: ServiceItem[] | undefined,
    amountOfColumns: number,
    columnLength: number
  ): ServiceItem[][] => {
    if (items === undefined) return [];
    let cursor = 0;

    // creating array filled with arrays, copied by value, not by reference
    const result = Array.from(
      { length: amountOfColumns },
      _ => [] as ServiceItem[]
    );

    // iterating over all items
    for (let i = 0; i < items.length; i++) {
      // cursor should increment after column equally filled
      const shouldIncrementCursor = (i + 1) / columnLength >= cursor + 1;

      // adding items in correct column
      result[cursor].push(items[i]);

      if (shouldIncrementCursor) {
        cursor++;
      }
    }

    return result;
  };

  const windowWidth = useWindowWidth();
  const amountOfColumns = getColumnsAmount(windowWidth);

  const itemsLength = items?.length;
  const columnLength = Math.ceil((itemsLength ?? 0) / amountOfColumns);

  const columns = useMemo(
    () => generateColumns(items, amountOfColumns, columnLength),
    [amountOfColumns, columnLength, items]
  );

  if (error) return <ErrorComponent refetch={refetch}/>;
  if (isLoading || !items) return <LoadingComponent />;

  return (
    <ErrorBoundary FallbackComponent={<ErrorComponent refetch={refetch}/>} >
      <div className={styles.serviceContainer}>
        {columns.map((column, index) => (
          <div key={index} className={styles.serviceColumn}>
            {column.map(col => (
              <ServiceComponent key={col.id} {...col} />
            ))}
          </div>
        ))}
      </div>
    </ErrorBoundary>
  );
});
